import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { Team, TeamMember, User, Workspace } from '@/hooks/api/models';
import { TeamMemberLogic } from '@/hooks/api/models/logics';

type Props = {
  children: ReactNode;
};

export type TeamMemberContextType = {
  _w_?: Workspace;
  set_w_: Dispatch<SetStateAction<Workspace | undefined>>;
  me?: User;
  setMe: Dispatch<SetStateAction<User | undefined>>;
  meTeamMember?: TeamMember;
  setMeTeamMember: Dispatch<SetStateAction<TeamMember | undefined>>;
  _t_?: Team;
  set_t_: Dispatch<SetStateAction<Team | undefined>>;
  isTeamAdmin: boolean;
  isApprover: boolean;
  isFinancialOfficer: boolean;
  isMember: boolean;
  isBackoffice: boolean;
};

export const TeamMemberContext = createContext<TeamMemberContextType>({
  _w_: undefined,
  set_w_: () => {},
  me: undefined,
  setMe: () => {},
  meTeamMember: undefined,
  setMeTeamMember: () => {},
  _t_: undefined,
  set_t_: () => {},
  isTeamAdmin: false,
  isApprover: false,
  isFinancialOfficer: false,
  isMember: false,
  isBackoffice: false,
});

export const TeamMemberProvider = ({ children }: Props) => {
  const [_w_, set_w_] = useState<Workspace>();
  const [me, setMe] = useState<User>();
  const [meTeamMember, setMeTeamMember] = useState<TeamMember>();
  const [_t_, set_t_] = useState<Team>();
  const [isTeamAdmin, setIsTeamAdmin] = useState<boolean>(false);
  const [isApprover, setIsApprover] = useState<boolean>(false);
  const [isFinancialOfficer, setIsFinancialOfficer] = useState<boolean>(false);
  const [isMember, setIsMember] = useState<boolean>(false);
  const [isBackoffice, setIsBackoffice] = useState<boolean>(false);

  useEffect(() => {
    if (meTeamMember) {
      setIsTeamAdmin((_) => TeamMemberLogic.hasMemberRole(meTeamMember));
      setIsApprover((_) => TeamMemberLogic.hasApproverRole(meTeamMember));
      setIsFinancialOfficer((_) => TeamMemberLogic.hasFinancialOfficerRole(meTeamMember));
      setIsMember((_) => TeamMemberLogic.hasMemberRole(meTeamMember));
      setIsBackoffice((_) => TeamMemberLogic.hasBackofficeRole(meTeamMember));
    }
  }, [meTeamMember]);

  return (
    <TeamMemberContext.Provider
      value={{
        _w_,
        set_w_,
        me,
        setMe,
        meTeamMember,
        setMeTeamMember,
        _t_,
        set_t_,
        isTeamAdmin,
        isApprover,
        isFinancialOfficer,
        isMember,
        isBackoffice,
      }}
    >
      {children}
    </TeamMemberContext.Provider>
  );
};

export const useCurrentTeamMember = () => {
  return useContext(TeamMemberContext);
};
